import { useEffect, useRef, useState } from 'react'

import { api } from '../api/client'
import { useTexte } from '../texte'
import type { MessagePage, Message } from '../api/types'

/**
 * Wie weit das Buch mit dem Einsammeln ist.
 *
 * **Es gibt keinen Gesamtwert, und das ist ehrlich so.** Der Server sagt nicht,
 * wie viele Nachrichten eine Reise hat — er sagt nur, ob es eine ältere Seite
 * gibt. Ein Fortschrittsbalken bräuchte ein Ganzes; hier steht deshalb eine
 * wachsende Zahl. Erfundene Prozente wären schlimmer als keine.
 */
export interface Sammelstand {
  /** Was bisher da ist, älteste zuerst. */
  nachrichten: Message[]
  /** Noch am Holen — solange darf nicht gedruckt werden. */
  laeuft: boolean
  /** Wie viele Seiten schon geholt sind. */
  seiten: number
  fehler: string | null
}

/**
 * Alle Nachrichten einer Reise holen, Seite für Seite.
 *
 * **Ein halbes Buch zu drucken wäre die stille Absage in Papierform.** Deshalb
 * sammelt dieser Haken bis zum Anfang der Reise und meldet erst dann `laeuft:
 * false`; der Druckknopf hängt daran.
 *
 * **Die Sichtbarkeitsregel steht nicht hier.** Ohne `with` liefert der Server
 * dem Absender ausschließlich `kind = 'broadcast'` (`Visibility::scope()`) —
 * das Buch ist damit vom Server her sauber, und hier wird nichts gefiltert und
 * nichts nachgeholfen. Mit `with` kommt der Verlauf mit genau einem Menschen.
 *
 * `before` ist die id der ältesten bisher geholten Nachricht; der Server gibt
 * sie als `next_before` zurück und `null`, wenn der Anfang erreicht ist.
 */
/**
 * Eine geholte Seite vor das bisher Gesammelte setzen — chronologisch.
 *
 * **Als eigene Funktion, weil hier ein Fehler steckte, den kein Browserlauf
 * finden konnte.** Der Kommentar an dieser Stelle behauptete, der Server
 * liefere je Seite aufsteigend; er liefert absteigend (seit #130 nach dem
 * Platz, `ORDER BY m.sort_key DESC, m.id DESC`). Zwei Dinge folgten daraus, und das zweite ist das schlimmere:
 *
 *  * Das Buch stand auf dem Kopf — neuester Beitrag zuerst. Aus dem Betrieb
 *    gemeldet: „die Reihenfolge des Berichts sollte andersherum sein, damit
 *    man das chronologisch lesen kann."
 *  * **Ab der zweiten Seite war die Reihenfolge nicht bloß umgekehrt, sondern
 *    durcheinander:** die Seiten standen von alt nach neu, innerhalb jeder
 *    Seite aber von neu nach alt. Bei einer Reise mit über zweihundert
 *    Beiträgen sprang das Buch alle zweihundert Zeilen zurück.
 *
 * Der zweite Fall braucht mehr als zweihundert Nachrichten, um sichtbar zu
 * werden — ein Browserlauf, der die erst anlegt, wäre eine Viertelstunde
 * beschäftigt. Als reine Rechnung ist er in zwei Zeilen zu prüfen, und genau
 * das tut `tools/pruefstand/buchreihenfolge.ts`.
 */
export function seiteEinfuegen(gesammelt: Message[], seite: Message[]): Message[] {
  // Die Seite kommt absteigend und liegt vor allem bisher Gesammelten: erst
  // umdrehen, dann vorn anhängen.
  return [...[...seite].reverse(), ...gesammelt]
}

export function useAlleNachrichten(tripId: number, mitBenutzer?: number): Sammelstand {
  // **Über `useTexte()` und nicht über `WORTLAUT`**: ein Hook läuft im
  // Zeichnen einer Komponente, der Ausgang gilt Hilfsfunktionen ohne eine.
  // Gefunden hat es der Textlauf, als `hooks/` in seine Liste kam (1.23.0).
  const t = useTexte()
  const [stand, setStand] = useState<Sammelstand>({
    nachrichten: [],
    laeuft: true,
    seiten: 0,
    fehler: null,
  })
  // **Ein Lauf je Reise und Gegenüber, und nicht zwei.** React ruft den Effekt
  // in der Entwicklung zweimal auf; ohne diese Sperre stünde jede Nachricht
  // doppelt im Buch.
  const laeuftFuer = useRef<string | null>(null)

  useEffect(() => {
    const schluessel = `${tripId}:${mitBenutzer ?? 0}`
    if (laeuftFuer.current === schluessel) return
    laeuftFuer.current = schluessel

    let abgebrochen = false
    let gesammelt: Message[] = []

    async function hole() {
      let before: number | null = null
      let seiten = 0
      // **The order may change while the book collects** (#130): an
      // insertion moves rows between pages already fetched and pages to
      // come. A changed `order_rev` starts over, a few times at most.
      let rev: number | undefined
      let neuAnfaenge = 0
      try {
        for (;;) {
          const frage: Record<string, string | number> = { limit: 200 }
          if (mitBenutzer !== undefined) frage.with = mitBenutzer
          if (before !== null) frage.before = before

          const seite: MessagePage = await api.get<MessagePage>(
            `/api/trips/${tripId}/messages`,
            frage,
          )
          if (abgebrochen) return
          if (rev !== undefined && seite.order_rev !== undefined && seite.order_rev !== rev && neuAnfaenge < 3) {
            neuAnfaenge++
            rev = undefined
            gesammelt = []
            before = null
            seiten = 0
            continue
          }
          rev ??= seite.order_rev

          // Der Server liefert je Seite **absteigend** und die Seiten von
          // neu nach alt -- `seiteEinfuegen` dreht beides um.
          gesammelt = seiteEinfuegen(gesammelt, seite.messages)
          seiten++
          setStand({ nachrichten: [...gesammelt], laeuft: true, seiten, fehler: null })

          if (seite.next_before === null) break
          before = seite.next_before
        }
        if (!abgebrochen) {
          setStand({ nachrichten: gesammelt, laeuft: false, seiten, fehler: null })
        }
      } catch (fehler) {
        if (abgebrochen) return
        // **Kein halbes Buch ohne Hinweis.** Was schon da ist, bleibt stehen —
        // aber der Knopf bleibt zu, und daneben steht, warum.
        setStand({
          nachrichten: gesammelt,
          laeuft: false,
          seiten,
          fehler: fehler instanceof Error ? fehler.message : t.allgemein.schiefgelaufen,
        })
      }
    }

    void hole()
    return () => {
      abgebrochen = true
      laeuftFuer.current = null
    }
  }, [tripId, mitBenutzer, t])

  return stand
}
